import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, database } from './firebase';
import { signOut } from 'firebase/auth';
import { ref, get, onValue } from 'firebase/database';
import CueScoreLink from './CueScoreLink';
import TournamentsList from './TournamentsList';
import VenuesManagement from './VenuesManagement';
import NotificationsManagement from './NotificationsManagement';
import { useImpersonation } from './ImpersonationContext';

function OrganiserDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [cuescoreLinkCollapsed, setCuescoreLinkCollapsed] = useState(true); // Collapsed by default
  const [cuescoreUsername, setCuescoreUsername] = useState(''); // Store username
  const [savedSessions, setSavedSessions] = useState([]);
  const [allOrganisers, setAllOrganisers] = useState([]); // For superAdmin impersonation dropdown
  
  // Impersonation context
  const { 
    isSuperAdmin, 
    isImpersonating, 
    impersonatedEmail,
    effectiveUserId,
    startImpersonation, 
    stopImpersonation 
  } = useImpersonation();
  
  // Responsive helper - visual only
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    // Check if user is logged in and load CueScore username
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        
        // Load CueScore username for effective user (could be impersonated)
        const usernameRef = ref(database, `organisers/${effectiveUserId || currentUser.uid}/cuescoreUsername`);
        const snapshot = await get(usernameRef);
        if (snapshot.exists()) {
          setCuescoreUsername(snapshot.val());
        }
      } else {
        // Not logged in, redirect to login
        navigate('/organiser-login');
      }
    });

    return () => unsubscribe();
  }, [navigate, effectiveUserId]);

  // Load all organisers for superAdmin impersonation dropdown
  useEffect(() => {
    if (!isSuperAdmin) return;
    
    const organisersRef = ref(database, 'organisers');
    const unsubscribe = onValue(organisersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const organiserList = Object.entries(data).map(([uid, org]) => ({
          uid,
          email: org.email || 'Unknown',
          cuescoreUsername: org.cuescoreUsername || '',
        }));
        setAllOrganisers(organiserList);
      }
    });
    
    return () => unsubscribe();
  }, [isSuperAdmin]);

  useEffect(() => {
    // Load all saved sessions from localStorage
    const sessions = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('pool_session_')) {
        const sessionId = key.replace('pool_session_', '');
        const data = JSON.parse(localStorage.getItem(key));
        sessions.push({
          id: sessionId,
          tableName: data.tableName || 'Unknown',
          p1Name: data.p1Name || 'Player 1',
          p2Name: data.p2Name || 'Player 2',
          timestamp: data.timestamp || Date.now()
        });
      }
    }
    // Sort by most recent first
    sessions.sort((a, b) => b.timestamp - a.timestamp);
    setSavedSessions(sessions);
  }, []);

  const clearAllSessions = () => {
    if (window.confirm('Are you sure you want to clear all saved sessions? This cannot be undone.')) {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('pool_session_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      setSavedSessions([]);
      alert('All sessions cleared!');
    }
  };

  const deleteSession = (sessionId) => {
    if (window.confirm(`Delete session ${sessionId}?`)) {
      localStorage.removeItem(`pool_session_${sessionId}`);
      setSavedSessions(savedSessions.filter(s => s.id !== sessionId));
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#0d1321',
      color: '#f0f2f5',
      padding: '24px',
      fontFamily: "'DM Sans', sans-serif"
    },
    header: {
      maxWidth: '1200px',
      margin: '0 auto',
      marginBottom: '32px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '16px' : '0',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center'
    },
    title: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: 'bold'
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    email: {
      color: '#8b95a8',
      fontSize: '14px'
    },
    button: {
      padding: '12px 24px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    signOutButton: {
      padding: '8px 16px',
      backgroundColor: '#1c2640',
      color: '#8b95a8',
      border: '1px solid #2a3555',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    card: {
      backgroundColor: '#151c2e',
      borderRadius: '14px',
      padding: isMobile ? '20px' : '32px',
      marginBottom: '24px',
      border: '1px solid #2a3555'
    },
    cardTitle: {
      fontSize: isMobile ? '20px' : '24px',
      fontWeight: 'bold',
      marginBottom: '16px',
      fontFamily: "'Outfit', sans-serif"
    }
  };

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', marginTop: '100px' }}>
          <p style={{ fontSize: '18px', color: '#8b95a8' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div style={{
          backgroundColor: '#f59e0b',
          color: '#000',
          padding: '12px 24px',
          textAlign: 'center',
          fontWeight: 'bold',
          marginBottom: '16px',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px'
        }}>
          <span>🎭 Viewing as: {impersonatedEmail}</span>
          <button
            onClick={stopImpersonation}
            style={{
              padding: '6px 12px',
              backgroundColor: '#000',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Stop Impersonating
          </button>
        </div>
      )}

      <div style={styles.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
            <img src="/icon.png" alt="Cue Clock Pro" style={{ width: '40px', height: '40px', borderRadius: '10px' }} />
            <div>
              <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '700', fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}>Cue Clock Pro</div>
              <div style={{ fontSize: '14px', color: '#8b95a8', fontWeight: '500' }}>Organiser Dashboard</div>
            </div>
          </div>
          <p style={styles.email}>Logged in as: {user.email}{isSuperAdmin && ' (Super Admin)'}</p>
        </div>
        <div style={styles.userInfo}>
          {/* SuperAdmin Impersonation Dropdown */}
          {isSuperAdmin && !isImpersonating && (
            <select
              onChange={(e) => {
                const selected = allOrganisers.find(o => o.uid === e.target.value);
                if (selected) {
                  startImpersonation(selected.uid, selected.email);
                }
              }}
              style={{
                padding: '8px 12px',
                backgroundColor: '#1c2640',
                color: '#f59e0b',
                border: '1px solid #f59e0b',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              defaultValue=""
            >
              <option value="" disabled>🎭 View as...</option>
              {allOrganisers
                .filter(o => o.uid !== user.uid)
                .map(org => (
                  <option key={org.uid} value={org.uid}>
                    {org.cuescoreUsername || org.email}
                  </option>
                ))
              }
            </select>
          )}
          <button
            onClick={() => window.open('/adhoc-clock-select', '_blank')}
            style={{
              ...styles.signOutButton,
              backgroundColor: '#10b981',
              color: 'white',
              border: '1px solid #10b981'
            }}
          >
            ⏱️ Ad-Hoc Clock
          </button>
          <button
            onClick={() => navigate('/player-grading')}
            style={{
              ...styles.signOutButton,
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: '1px solid #8b5cf6'
            }}
          >
            🎱 Player Grading
          </button>
          <button
            onClick={() => navigate('/book')}
            style={{
              ...styles.signOutButton,
              backgroundColor: '#059669',
              color: 'white',
              border: '1px solid #059669'
            }}
          >
            🎰 Odds Engine
          </button>
          <button onClick={() => navigate('/')} style={styles.signOutButton}>
            🏠 Home
          </button>
          <button onClick={handleSignOut} style={styles.signOutButton}>
            🚪 Sign Out
          </button>
        </div>
      </div>

      <div style={styles.content}>
        {/* Welcome Section */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            {cuescoreUsername ? `Welcome, ${cuescoreUsername}! 🎱` : 'Welcome, Organiser! 🎱'}
          </h2>
          <p style={{ color: '#8b95a8' }}>
            Manage your pool tournament matches from this dashboard.
          </p>
        </div>

        {/* Tournaments Section */}
        <TournamentsList />
        
        {/* Venues Section */}
        <VenuesManagement />

        {/* Player Notifications Section */}
        <NotificationsManagement />

        {/* CueScore Link Section - LAST (Collapsible) */}
        <div style={styles.card}>
          <div 
            onClick={() => setCuescoreLinkCollapsed(!cuescoreLinkCollapsed)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              padding: '12px',
              backgroundColor: '#1c2640',
              borderRadius: '10px',
              marginBottom: cuescoreLinkCollapsed ? '0' : '16px'
            }}
          >
            <span style={{fontSize: '18px', fontWeight: 'bold', flex: 1}}>
              🔗 CueScore Account Settings
            </span>
            <span style={{fontSize: '20px', color: '#5a6478'}}>
              {cuescoreLinkCollapsed ? '▶' : '▼'}
            </span>
          </div>

          {!cuescoreLinkCollapsed && (
            <div style={{marginTop: '16px'}}>
              <CueScoreLink />
            </div>
          )}
        </div>

        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={styles.cardTitle}>💾 Saved Sessions ({savedSessions.length})</h2>
            {savedSessions.length > 0 && (
              <button
                onClick={clearAllSessions}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                🗑️ Clear All Sessions
              </button>
            )}
          </div>

          {savedSessions.length === 0 ? (
            <p style={{ color: '#8b95a8' }}>
              No saved sessions.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {savedSessions.map(session => (
                <div
                  key={session.id}
                  style={{
                    backgroundColor: '#1c2640',
                    padding: '16px',
                    borderRadius: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid #2a3555'
                  }}
                >
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                      {session.tableName}
                    </div>
                    <div style={{ fontSize: '14px', color: '#8b95a8' }}>
                      {session.p1Name} vs {session.p2Name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#5a6478', marginTop: '4px' }}>
                      Session: {session.id}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => navigate(`/match/${session.id}`)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      📂 Resume
                    </button>
                    <button
                      onClick={() => deleteSession(session.id)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#1c2640',
                        color: 'white',
                        border: '1px solid #2a3555',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrganiserDashboard;